import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is yield on cost (YoC)?',
    a: 'Yield on cost is the dividend you receive this year divided by what you originally paid per share. If you bought Johnson & Johnson at $50 and it now pays $5 per share annually, your yield on cost is 10% — regardless of where the stock trades today. Current yield uses today\u2019s price; YoC uses your price.',
  },
  {
    q: 'Why does yield on cost matter?',
    a: 'It is the only yield number that reflects YOUR experience as a holder. A new buyer looks at JNJ today and sees a 3% yield; a 20-year holder who paid $50 sees a 10% YoC. The same dividend, two very different stories. For long-term dividend investors, YoC is the metric that captures the compounding of dividend hikes against a fixed cost basis.',
  },
  {
    q: 'Is a high YoC always good?',
    a: 'Not necessarily. A high YoC just means the stock has raised its dividend a lot since you bought. That is usually a sign of quality, but it can also mean the stock price has stagnated or fallen, pushing YoC up because your cost basis is now relatively low. Always check the current yield AND the dividend growth rate side by side.',
  },
  {
    q: 'How is YoC different from current yield?',
    a: 'Current yield uses the live stock price as the denominator. YoC uses your average cost per share. New money you put in today buys at the current yield. Old money you have held for years earns the YoC. Both are real; they just answer different questions.',
  },
  {
    q: 'Does DiviDrip show yield on cost?',
    a: 'Yes. On the My Portfolio tab inside the Stock Modal, the "Yield on Cost" tile shows your YoC for that ticker based on the total cost across all your lots (regular buys + DRIP buys). The portfolio-level YoC is shown on the Portfolio Summary section at the top of the My Portfolio page.',
  },
];

export default function LearnYieldOnCost() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Yield on Cost (YoC) Explained — The Long-Term Dividend Investor's Real Yield | DiviDrip"
      description="What yield on cost means, why it differs from current yield, the math behind it, and why 20-year dividend holders care about YoC over headline yield."
      canonical="https://twylightcrow.com/learn/yield-on-cost"
      guidePath="/learn/yield-on-cost"
      sectionLabel="Metrics & Evaluation"
      sectionAnchor="metrics"
      kicker="Learn · Yield metrics"
      h1="Yield on Cost — Your Real Long-Term Dividend Yield"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/four-phases', label: 'The 4 phases of a dividend investor\u2019s life' },
        { to: '/learn/boring-is-beautiful', label: 'Why dividend growers beat glamour stocks' },
        { to: '/learn/what-is-drip', label: 'What is DRIP? Reinvestment explained' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Dividend Kings vs Aristocrats vs Achievers' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — tax guide' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        Most investors quote one yield number — the headline percentage on a stock&apos;s summary page.
        It tells you what TODAY&apos;S buyer earns. But if you have held a stock for years
        and watched it raise the dividend, the headline number tells you almost nothing
        about YOUR experience as a holder.
      </p>
      <p>
        That is what <strong>yield on cost</strong> (YoC) captures. And once you start
        thinking in YoC, the dividend-growth investing thesis suddenly makes sense.
      </p>

      <h2>The math (it&apos;s simpler than the name)</h2>
      <p>
        <code>Yield on Cost = (Current annual dividend per share) ÷ (Your cost basis per share)</code>
      </p>
      <p>Three quick examples:</p>
      <ul>
        <li>You buy <strong>SCHD</strong> at $70 today; it pays $2.80/share annually. Current yield = 4.0%. YoC = 4.0% (same, since you just bought).</li>
        <li>Ten years from now, SCHD has raised the dividend 8% a year. It now pays $6.05. The price is $130. <strong>Current yield: 4.65%. Your YoC: 8.64%.</strong></li>
        <li>Another fifteen years pass. SCHD now pays $19.16. Price $260. <strong>Current yield: 7.4%. Your YoC: 27.4%.</strong></li>
      </ul>
      <p>
        That is the entire dividend-growth story. YoC compounds against your cost basis,
        which never moves. The bigger the dividend grows, the wilder your YoC looks
        — and the less it matters whether the stock price keeps up, because the income
        is doing the work.
      </p>

      <h2>YoC vs current yield — which to use when</h2>
      <ul>
        <li><strong>Adding new money?</strong> Use current yield. New dollars buy at today&apos;s price.</li>
        <li><strong>Evaluating your existing position?</strong> Use YoC. It tells you what your held shares are earning relative to what you paid.</li>
        <li><strong>Comparing two stocks you don&apos;t own?</strong> Use current yield + dividend growth rate (CAGR). YoC is meaningless for stocks you have not bought yet.</li>
        <li><strong>Selling decision?</strong> Both. A high YoC tells you the position has performed; current yield tells you whether you could reinvest the proceeds at a comparable rate elsewhere.</li>
      </ul>

      <h2>The catch — DRIP and lot-blending</h2>
      <p>
        If you DRIP, every reinvested share is bought at the THEN-current price, not your
        original price. So your average cost basis drifts upward over time as DRIP buys
        at higher prices stack onto your original cheaper lots. Your YoC drifts down
        slightly compared to a pure no-DRIP hold.
      </p>
      <p>
        This is not a bug — you genuinely own more shares — but it is why DiviDrip tracks
        each lot separately and shows you both: per-lot YoC (the pure number for each buy)
        AND blended YoC (across the position). Pure dividend-growth purists watch the
        lot-level YoC on their oldest buys. Most investors track the blended one.
      </p>

      <h2>How to think about a 25%+ YoC</h2>
      <p>
        If you see a long-term holder bragging about a 30% YoC on Coca-Cola or Realty
        Income, it is not magic — it is just dividend growth running for 30+ years against
        a fixed cost basis. The stock can be at a perfectly normal 3-4% current yield;
        the holder&apos;s YoC just reflects that they bought at $5 instead of $60.
      </p>
      <p>
        Two takeaways:
      </p>
      <ol>
        <li>YoC is a backward-looking reward for patience, not a forward-looking forecast.</li>
        <li>Time-in-market is the single biggest YoC driver. Starting in your 20s vs your 50s is the difference between a 25% YoC at retirement and a 5% YoC.</li>
      </ol>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, click any position you have held for
        more than a year, switch to the <strong>My Portfolio</strong> tab inside the
        Stock Modal, and look at the Yield on Cost tile. Then look at the same stock&apos;s
        current yield on the <strong>Dividend Info</strong> tab. The gap between the two is your
        reward for sitting still.
      </p>
    </LearnPage>
  );
}
